import { create } from 'zustand'

export const SUPPORTED_LOCALES = ['zh-CN', 'en-US'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'zh-CN'
export const LOCALE_STORAGE_KEY = 'texture-merge.locale'

export const isSupportedLocale = (locale: string | null): locale is Locale =>
  SUPPORTED_LOCALES.includes(locale as Locale)

export const getStoredLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE

  try {
    const locale = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    return isSupportedLocale(locale) ? locale : DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

const persistLocale = (locale: Locale) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Ignore storage failures so language switching remains usable in memory.
  }
}

interface UIState {
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  theme: 'light'
  locale: Locale
  setLocale: (locale: Locale) => void
  toggleLocale: () => Locale
}

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  theme: 'light',
  locale: getStoredLocale(),
  setLocale: (locale) => {
    persistLocale(locale)
    set({ locale })
  },
  toggleLocale: () => {
    let nextLocale: Locale = DEFAULT_LOCALE

    set((state) => {
      nextLocale = state.locale === 'zh-CN' ? 'en-US' : 'zh-CN'
      persistLocale(nextLocale)
      return { locale: nextLocale }
    })

    return nextLocale
  },
}))
